import React from 'react';

import { SortableTable } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Text from '~/components/core/TextComponents/Text';
import Switch from '~/components/ui/atoms/Switch';
import { serverDateToLocal } from '~/DateTimeUtils';
import type { ESignConfigResponse } from '~/server_shared/openapi/data-contracts';

interface ESignConfigurationCardProps {
  configuration: ESignConfigResponse;
  onEnable: () => void;
  onDisable: () => void;
  isLoading: boolean;
}

const ESignConfigurationCard: React.FC<ESignConfigurationCardProps> = ({
  configuration,
  onEnable,
  onDisable,
  isLoading,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return serverDateToLocal(dateString);
  };

  // Use the configuration directly as table data
  const tableData: ESignConfigResponse[] = [configuration];

  const columnData = [
    { id: 'id', isHidden: true },
    {
      id: 'is_enabled',
      label: 'Status',
      specialCell: (row: ESignConfigResponse) => (
        <Chip
          label={row.is_enabled ? 'Enabled' : 'Disabled'}
          color={row.is_enabled ? 'primary' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'vendor_type',
      label: 'Vendor',
      specialCell: (row: ESignConfigResponse) => (
        <Text variant={Text.VARIANTS.SM} className="capitalize">
          {row.vendor_type.replace(/_/g, ' ')}
        </Text>
      ),
    },
    {
      id: 'created_datetime',
      label: 'Created',
      specialCell: (row: ESignConfigResponse) => formatDate(row.created_datetime),
    },
    {
      id: 'last_updated_datetime',
      label: 'Last Updated',
      specialCell: (row: ESignConfigResponse) => (
        <div>
          <Text variant={Text.VARIANTS.SM}>{formatDate(row.last_updated_datetime)}</Text>
          {row.last_updated_by_username && (
            <Text variant={Text.VARIANTS.XS} className="text-gray-500">
              by {row.last_updated_by_username}
            </Text>
          )}
        </div>
      ),
    },
    {
      id: 'enable',
      label: 'Enable',
      disableSort: true,
      specialCell: (row: ESignConfigResponse) => (
        <Switch
          checked={!!row.is_enabled}
          onChange={() => (row.is_enabled ? onDisable() : onEnable())}
          disabled={isLoading}
        />
      ),
    },
  ];

  return <SortableTable defaultOrderColumn={0} columns={columnData} rows={tableData} hideHeader={false} />;
};

export default ESignConfigurationCard;
